/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { throttle } from '@ember/runloop';
import AnalyticsService from 'cio/services/analytics';
import SubscriptionLanguageModel from 'cio/models/subscription-language';
import { FlyKeyboardEvent } from '@customerio/fly-components/types/fly-components/events';
import { isEmpty } from '@ember/utils';
import RouterService from '@ember/routing/router-service';
import { dropTask } from 'ember-concurrency';
import ApiErrorsService from 'cio/services/api-errors';
import SubscriptionCenterService from 'cio/services/subscription-center';

export default class LanguageManagement extends Component<never> {
  @service analytics!: AnalyticsService;
  @service subscriptionCenter!: SubscriptionCenterService;
  @service router!: RouterService;
  @service apiErrors!: ApiErrorsService;

  @tracked selectedLanguages: SubscriptionLanguageModel[] = [];
  @tracked searchValue = '';
  @tracked showConfirmDeleteModal = false;

  constructor(owner: unknown, args: never) {
    super(owner, args);

    this.subscriptionCenter.initializeTask.perform();
  }

  get filteredLanguages() {
    if (isEmpty(this.searchValue)) {
      return this.subscriptionCenter.languages;
    }

    return this.subscriptionCenter.languages.filter(
      (language) =>
        language.id.toLowerCase().includes(this.searchValue.toLowerCase()) ||
        language.countryName?.toLowerCase().includes(this.searchValue.toLowerCase()),
    );
  }

  get isMakeNotLiveDisabled() {
    return !this.selectedLanguages.some((language) => language.enabled);
  }

  get someSelectedLanguagesMissingContent() {
    return this.selectedLanguages.some((language) => language.isMissingContent);
  }

  get isMakeLiveDisabled() {
    return this.someSelectedLanguagesMissingContent || !this.selectedLanguages.some((language) => !language.enabled);
  }

  get allSelected() {
    if (this.selectedLanguages == null || this.filteredLanguages == null || this.filteredLanguages.length === 0) {
      return false;
    }

    return this.selectedLanguages.length === this.filteredLanguages.length;
  }

  get allLanguagesOnPageIndeterminate() {
    return this.selectedLanguages.length > 0 && !this.allSelected;
  }

  @action
  searchLanguages(event: FlyKeyboardEvent<HTMLInputElement>) {
    throttle(this, this.handleSearchValue, event, 100, false);
  }

  @action
  handleSearchValue(event: FlyKeyboardEvent<HTMLInputElement>) {
    this.searchValue = event.target.value;
  }

  @action
  startEditLanguage() {
    this.analytics.track('subscription_center', 'edit_languages_started');
  }

  @action
  selectAllLanguagesOnPage(checked: boolean) {
    if (checked) {
      this.selectedLanguages = [...this.filteredLanguages];
    } else {
      this.selectedLanguages = [];
    }
  }

  @action
  languageChecked(language: SubscriptionLanguageModel, index: number, checked: boolean) {
    if (checked) {
      this.selectedLanguages = [...this.selectedLanguages, language];
    } else {
      this.selectedLanguages = this.selectedLanguages.filter((selectedLang) => selectedLang !== language);
    }
  }

  @action
  deleteLanguages() {
    this.subscriptionCenter.deleteLanguages.perform(this.selectedLanguages);
    this.selectedLanguages = [];
    this.showConfirmDeleteModal = false;
  }

  @action
  onLanguageAdd(languages: string[]) {
    this.router.transitionTo('workspace-settings.subscription_center.localization.edit', {
      queryParams: { lang: languages.sort()[0] },
    });
  }

  enableDisableLanguages = dropTask(async (enabled: boolean) => {
    try {
      await this.subscriptionCenter.enableDisableLanguages.perform(this.selectedLanguages, enabled);
      this.selectedLanguages = [];
    } catch (err) {
      this.apiErrors.report(err);
    }
  });
}
